import {
  ChevronDownIcon,
  ChevronRightIcon as ChevronRightOutlineIcon,
  DocumentIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import {
  CameraIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import React from 'react';

import { ChatBubbleMultipleIcon } from '@/components/icons/ChatBubbleMultiple.icon';
import { KeyIcon } from '@/components/icons/Key.icon';
import { Tooltip } from '@/components/Tooltip';
import { cn } from '@/utils/classname';

import { getRowPath } from './getRowPath';
import {
  SignalKey,
  SitemapRowHandlers,
  WebpageTreeNode,
  WebpageWithRisk,
} from './types';
import { SIGNAL_DEFS } from './webpageSignals';

interface SitemapTreeRowProps {
  node: WebpageTreeNode;
  level: number;
  /** Is this node the last among its siblings? Drives └── vs ├──. */
  isLast: boolean;
  /** Vertical-guide flags for intermediate ancestors (see FlatRow.pathFlags). */
  pathFlags: boolean[];
  /** Only meaningful for expandable nodes. */
  isExpanded: boolean;
  onToggle: (path: string) => void;
  handlers: SitemapRowHandlers;
}

/**
 * `tree`-style connector guides for one row: a fixed-width slot per ancestor
 * (a vertical │ where the ancestor has a following sibling) plus an elbow
 * (└── if this node is the last sibling, ├── otherwise) at the node's own
 * indent. Purely decorative.
 */
const TreeGuides: React.FC<{
  level: number;
  isLast: boolean;
  pathFlags: boolean[];
}> = ({ level, isLast, pathFlags }) => {
  if (level === 0) return null;
  return (
    <>
      {pathFlags.map((show, i) => (
        <span
          key={i}
          className="relative w-3 shrink-0 self-stretch"
          aria-hidden
        >
          {show && (
            <span className="border-chariot-stroke absolute inset-y-0 left-1/2 border-l" />
          )}
        </span>
      ))}
      <span className="relative w-3 shrink-0 self-stretch" aria-hidden>
        {/* vertical: top->middle always; continues to bottom unless last child */}
        <span
          className={cn(
            'border-chariot-stroke absolute top-0 left-1/2 border-l',
            isLast ? 'h-1/2' : 'bottom-0'
          )}
        />
        {/* horizontal: middle of slot -> right edge */}
        <span className="border-chariot-stroke absolute top-1/2 right-0 left-1/2 border-t" />
      </span>
    </>
  );
};

function renderSignalIcon(
  key: SignalKey,
  wp: WebpageWithRisk,
  handlers: SitemapRowHandlers
): React.ReactNode {
  switch (key) {
    case 'secrets':
      return (
        <Tooltip title="Secrets identified - click to view vulnerabilities">
          <button
            type="button"
            className="cursor-pointer"
            onClick={() => handlers.onOpenVulnerability(wp)}
            aria-label="View secret vulnerabilities"
          >
            <KeyIcon className="text-chariot-brand-primary size-5" />
          </button>
        </Tooltip>
      );
    case 'login':
      return (
        <Tooltip title="Login form identified - click to view vulnerabilities">
          <button
            type="button"
            className="cursor-pointer"
            onClick={() => handlers.onOpenVulnerability(wp)}
            aria-label="View login vulnerabilities"
          >
            <CheckCircleIcon className="text-chariot-brand-primary size-5" />
          </button>
        </Tooltip>
      );
    case 'sso': {
      const hasSSO = Object.keys(wp.sso_identified || {}).length > 0;
      return hasSSO ? (
        <Tooltip title="SSO protection identified">
          <CheckCircleIcon className="text-chariot-brand-primary size-5" />
        </Tooltip>
      ) : (
        <Tooltip title="No SSO protection identified">
          <XMarkIcon className="text-chariot-text-disabled size-5 stroke-[3px]" />
        </Tooltip>
      );
    }
    case 'screenshots':
      return (
        <Tooltip title="Screenshot available - click to view">
          <CameraIcon
            className="text-chariot-brand-primary size-5 cursor-pointer stroke-[3px]"
            onClick={() => handlers.onOpenScreenshot(wp)}
          />
        </Tooltip>
      );
    case 'response':
      return (
        <Tooltip title="Response captured - click to download">
          <button
            type="button"
            className="border-chariot-brand-primary bg-chariot-background-secondary text-chariot-brand-primary mx-auto flex w-fit cursor-pointer items-center gap-2 rounded-full border py-1 pr-2 pl-3"
            onClick={event => {
              event.stopPropagation();
              handlers.onDownloadResponse(wp);
            }}
            aria-label="Download response"
          >
            <ChatBubbleMultipleIcon className="size-4" />
            <ChevronRightIcon className="size-4 stroke-[3px]" />
          </button>
        </Tooltip>
      );
    default:
      return null;
  }
}

/**
 * A single table row in the sitemap. The Path cell carries the tree affordances
 * (indent, +/- expand toggle, folder/page icon, name); the remaining cells are
 * the per-signal columns, populated only when the node is a page whose signal
 * predicate matches.
 */
export const SitemapTreeRow: React.FC<SitemapTreeRowProps> = ({
  node,
  level,
  isLast,
  pathFlags,
  isExpanded,
  onToggle,
  handlers,
}) => {
  const isExpandable = Boolean(node.children?.length);
  const wp = node.webpage;
  const fullPath = getRowPath(node);

  return (
    <tr className="hover:bg-chariot-background-secondary">
      {/*
        No vertical padding on this cell: the tree guides live in a full-height
        column so their vertical lines meet the next row's edge-to-edge (a
        continuous │ instead of dashes with gaps). Row breathing room is on the
        content wrapper below and the signal cells, which set the row height.
      */}
      <td className="pr-2">
        <div className="flex h-full items-stretch">
          <TreeGuides level={level} isLast={isLast} pathFlags={pathFlags} />
          <div className="flex min-w-0 flex-1 items-center gap-2 py-0.5 pl-1">
            {isExpandable ? (
              <button
                type="button"
                className="text-chariot-text-secondary hover:text-chariot-text-primary focus-visible:ring-ring shrink-0 cursor-pointer rounded focus:outline-hidden focus-visible:ring-1"
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.name}`}
                aria-expanded={isExpanded}
                onClick={() => onToggle(node.path)}
              >
                {isExpanded ? (
                  <ChevronDownIcon className="size-4" />
                ) : (
                  <ChevronRightOutlineIcon className="size-4" />
                )}
              </button>
            ) : (
              <span className="size-4 shrink-0" aria-hidden />
            )}

            {isExpandable ? (
              <FolderIcon
                className="text-chariot-text-secondary size-4 shrink-0"
                aria-label="Folder"
              />
            ) : (
              <DocumentIcon
                className="text-chariot-text-secondary size-4 shrink-0"
                aria-label="Page"
              />
            )}

            {wp ? (
              // A page (possibly also a folder): clicking the name opens the URL;
              // the tooltip reveals the full URL when the name is truncated.
              <Tooltip title={fullPath} placement="right">
                <button
                  type="button"
                  className="text-chariot-text-primary hover:text-chariot-brand-primary min-w-0 cursor-pointer truncate text-left text-sm"
                  onClick={() => handlers.onOpenUrl(wp)}
                >
                  {node.name}
                </button>
              </Tooltip>
            ) : (
              // A pure folder: clicking the name toggles expansion; the tooltip
              // shows the folder's full path.
              <Tooltip title={fullPath} placement="right">
                <button
                  type="button"
                  className="text-chariot-text-primary min-w-0 cursor-pointer truncate text-left text-sm font-medium"
                  onClick={() => onToggle(node.path)}
                >
                  {node.name}
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </td>

      {SIGNAL_DEFS.map(def => {
        // SSO also renders an explicit "no protection" X on login pages that
        // lack SSO; the other signals render only when their predicate matches.
        const show =
          !!wp &&
          (def.predicate(wp) ||
            (def.key === 'sso' && wp.type === 'authentication'));
        return (
          <td key={def.key} className="px-2 py-0.5">
            <div className="flex items-center justify-center">
              {show ? renderSignalIcon(def.key, wp!, handlers) : null}
            </div>
          </td>
        );
      })}
    </tr>
  );
};
